/**
 * 经纪工作台
 * 未入驻 → 入驻申请表单（3101 经纪资质未审核通过文案）
 * 已入驻 → 可接委托 / 撮合中 / 成交管理 / 佣金结算
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  acceptBrokerOrder,
  applyBroker,
  listBrokerOrders,
  matchBrokerOrder,
  workbenchStats,
} from '@/api/broker';
import type { BrokerOrder, WorkbenchStats } from '@/types';
import { formatDateTime, formatMoney } from '@/utils/format';
import StatusTag from '@/components/common/StatusTag';
import Loading from '@/components/common/Loading';
import ErrorBox from '@/components/common/ErrorBox';
import Empty from '@/components/common/Empty';
import PageHero from '@/components/common/PageHero';
import '@/styles/pages/broker.css';

type Section = 'available' | 'matching' | 'matched' | 'commission';

export default function BrokerWorkbench() {
  const [stats, setStats] = useState<WorkbenchStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [section, setSection] = useState<Section>('available');
  const [orders, setOrders] = useState<BrokerOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [tip, setTip] = useState('');

  // 入驻申请表单（对齐后端：real_name/phone/cert_no 必填；license_no 等价 cert_no）
  const [form, setForm] = useState({
    real_name: '',
    phone: '',
    cert_no: '',
  });
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const data = await workbenchStats();
      setStats(data);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const view =
        section === 'available'
          ? 'available'
          : section === 'matching'
            ? 'mine'
            : section === 'matched'
              ? 'matched'
              : 'matched';
      const data = await listBrokerOrders(view);
      setOrders(
        section === 'matching'
          ? data.filter((o) => o.status === 1)
          : section === 'commission'
            ? data.filter((o) => o.status === 2)
            : data,
      );
    } finally {
      setOrdersLoading(false);
    }
  }, [section]);

  useEffect(() => {
    if (stats?.broker) {
      loadOrders();
    }
  }, [section, stats?.broker, loadOrders]);

  const handleApply = async () => {
    setFormError('');
    if (!form.real_name.trim() || !form.phone.trim() || !form.cert_no.trim()) {
      setFormError('请完整填写入驻申请（姓名/电话/证照号）');
      return;
    }
    setSubmitting(true);
    try {
      await applyBroker({
        real_name: form.real_name,
        phone: form.phone,
        cert_no: form.cert_no,
      });
      await loadStats();
      setTip('入驻申请已提交，平台审核通过后即可接单（经纪资质未审核通过将提示 3101）');
    } catch (e) {
      setFormError(e instanceof Error ? e.message : '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAccept = async (id: number) => {
    setBusyId(id);
    try {
      await acceptBrokerOrder(id);
      setTip('接单成功，进入撮合中');
      await Promise.all([loadStats(), loadOrders()]);
    } finally {
      setBusyId(null);
    }
  };

  const handleMatch = async (id: number) => {
    if (!window.confirm('确认撮合完成并生成关联交易订单吗？')) return;
    setBusyId(id);
    try {
      const res = await matchBrokerOrder(id);
      setTip(`撮合完成，已生成关联订单 #${res.linked_order_id}`);
      await Promise.all([loadStats(), loadOrders()]);
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="broker-page">
        <PageHero eyebrow="BROKER WORKBENCH" title="经纪工作台" desc="入驻申请 · 接单撮合 · 佣金结算" />
        <div className="container">
          <Loading rows={5} />
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="broker-page">
        <PageHero eyebrow="BROKER WORKBENCH" title="经纪工作台" desc="入驻申请 · 接单撮合 · 佣金结算" />
        <div className="container">
          <ErrorBox message={error} onRetry={loadStats} />
        </div>
      </div>
    );
  }

  const broker = stats?.broker;

  return (
    <div className="broker-page">
      <PageHero eyebrow="BROKER WORKBENCH" title="经纪工作台" desc="入驻申请 · 接单撮合 · 佣金结算">
        <Link className="btn btn-outline-light btn-sm" to="/broker/orders">委托大厅</Link>
      </PageHero>

      <div className="container">
        {tip ? (
          <div className="broker-tip" onClick={() => setTip('')}>{tip} ✕</div>
        ) : null}

        {!broker ? (
          <div className="broker-apply">
            <div className="broker-apply__head">
              <strong>经纪入驻申请</strong>
              <span>提交后由平台审核（错误码 3101：经纪资质未审核通过）</span>
            </div>
            <div className="broker-form-grid">
              <div className="form-group">
                <label className="form-label"><span className="req">*</span>姓名</label>
                <input className="form-control" value={form.real_name} onChange={(e) => setForm({ ...form, real_name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label"><span className="req">*</span>联系电话</label>
                <input className="form-control" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label"><span className="req">*</span>资质证照号</label>
                <input className="form-control" placeholder="营业执照 / 经纪资质证号" value={form.cert_no} onChange={(e) => setForm({ ...form, cert_no: e.target.value })} />
              </div>
            </div>
            {formError ? <div className="form-error">{formError}</div> : null}
            <button className="btn btn-primary btn-lg" disabled={submitting} onClick={handleApply}>
              {submitting ? '提交中…' : '提交入驻申请'}
            </button>
          </div>
        ) : (
          <>
            {/* 统计卡 */}
            <div className="broker-stats">
              <div><b>{stats?.available_count ?? 0}</b><span>可接委托</span></div>
              <div><b>{stats?.matching_count ?? 0}</b><span>撮合中</span></div>
              <div><b>{stats?.matched_count ?? 0}</b><span>已成交</span></div>
              <div><b>{formatMoney(stats?.commission_total ?? 0)}</b><span>累计佣金</span></div>
              <div><b>{formatMoney(stats?.pending_commission ?? 0)}</b><span>待结算</span></div>
            </div>

            <div className="broker-workbench">
              <aside className="broker-menu">
                {(
                  [
                    ['available', '可接委托'],
                    ['matching', '撮合中'],
                    ['matched', '成交管理'],
                    ['commission', '佣金结算'],
                  ] as [Section, string][]
                ).map(([key, label]) => (
                  <button key={key} className={`broker-menu__item ${section === key ? 'active' : ''}`} onClick={() => setSection(key)}>
                    {label}
                  </button>
                ))}
              </aside>

              <section className="broker-content">
                {ordersLoading ? (
                  <Loading rows={4} type="list" />
                ) : orders.length === 0 ? (
                  <Empty
                    icon="📭"
                    title={section === 'available' ? '暂无待接委托' : section === 'matching' ? '暂无撮合中委托' : '暂无成交记录'}
                    desc={section === 'available' ? '可去委托大厅看看其他可接单的委托' : ''}
                    action={
                      section === 'available' ? (
                        <Link className="btn btn-outline btn-sm" to="/broker/orders?tab=available">去委托大厅</Link>
                      ) : undefined
                    }
                  />
                ) : (
                  <div className="broker-list">
                    {orders.map((o) => (
                      <div className="broker-card" key={o.id}>
                        <div className="broker-card__head">
                          <StatusTag type="broker" status={o.status} />
                          <span className={`broker-card__type ${o.order_type === 1 ? 'buy' : 'sell'}`}>
                            {o.order_type === 1 ? '求购' : '求售'}
                          </span>
                          <span className="broker-card__no">{o.order_no}</span>
                        </div>
                        <div className="broker-card__body">
                          <div className="broker-card__meta">
                            <span>品种：<b>{o.cattle_type ?? '-'}</b></span>
                            <span>数量：<b>{o.quantity} 头</b></span>
                            <span>价格：<b>{formatMoney(o.price_min)} ~ {formatMoney(o.price_max)}</b></span>
                            <span>佣金：<b>{(o.commission_rate ?? 0.02) * 100}%</b></span>
                            <span>发布：<b>{formatDateTime(o.created_at)}</b></span>
                          </div>
                          <div className="broker-card__publisher">发布方：{o.publisher_name ?? `用户${o.publisher_id}`}</div>
                        </div>
                        <div className="broker-card__ops">
                          {section === 'available' ? (
                            <button className="btn btn-gold btn-sm" disabled={busyId === o.id} onClick={() => handleAccept(o.id)}>
                              {busyId === o.id ? '接单中…' : '接单'}
                            </button>
                          ) : null}
                          {section === 'matching' ? (
                            <button className="btn btn-primary btn-sm" disabled={busyId === o.id} onClick={() => handleMatch(o.id)}>
                              {busyId === o.id ? '提交中…' : '录入成交'}
                            </button>
                          ) : null}
                          {section === 'matched' && o.linked_order_id ? (
                            <Link className="btn btn-outline btn-sm" to={`/orders/${o.linked_order_id}`}>查看订单</Link>
                          ) : null}
                          {section === 'commission' ? (
                            <span className="broker-commission">
                              佣金约 {formatMoney(((o.price_min ?? 0) + (o.price_max ?? 0)) / 2 * o.quantity * (o.commission_rate ?? 0.02))}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
